// frontend/src/components/Invoices/InvoiceDetails.jsx
import React, { useState } from 'react';
import { transactionService } from '../../services/api';
import { toast } from 'react-hot-toast';

export default function InvoiceDetails({ invoice, onClose, onUpdate }) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(invoice?.balance_due || 0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [processing, setProcessing] = useState(false);

  // In InvoiceDetails.jsx - handleRecordPayment
const handleRecordPayment = async () => {
    if (paymentAmount <= 0) {
        toast.error('Please enter a valid amount');
        return;
    }

    if (paymentAmount > invoice.balance_due) {
        toast.error(`Amount cannot exceed balance due of KES ${invoice.balance_due}`);
        return;
    }

    setProcessing(true);
    try {
        // ✅ Ensure amount is sent as a number
        await transactionService.recordPayment(invoice.id, {
            amount: parseFloat(paymentAmount),  // Convert to number
            payment_method: paymentMethod,
            reference: `Payment for invoice ${invoice.invoice_number}`
        });
        
        toast.success(`Payment of KES ${paymentAmount.toLocaleString()} recorded successfully`);
        setShowPaymentModal(false);
        onUpdate();
    } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
        setProcessing(false);
    }
};

  return (
    <div className="space-y-4">
      {/* Invoice details display */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice #</label>
          <p className="mt-1 text-sm text-gray-900">{invoice?.invoice_number}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <p className="mt-1">
            <span className={`px-2 py-1 text-xs rounded-full ${
              invoice?.status === 'paid' ? 'bg-green-100 text-green-800' :
              invoice?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {invoice?.status?.toUpperCase()}
            </span>
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Total Amount</label>
          <p className="mt-1 text-sm font-bold text-gray-900">KES {invoice?.total_amount?.toLocaleString()}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Amount Paid</label>
          <p className="mt-1 text-sm text-green-600">KES {invoice?.amount_paid?.toLocaleString()}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Balance Due</label>
          <p className="mt-1 text-sm text-red-600 font-semibold">KES {invoice?.balance_due?.toLocaleString()}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Due Date</label>
          <p className="mt-1 text-sm text-gray-900">{new Date(invoice?.due_date).toLocaleDateString()}</p>
        </div>
      </div>

      {/* Payment Button - Only show if not fully paid */}
      {invoice?.status !== 'paid' && invoice?.balance_due > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowPaymentModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Record Payment
          </button>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Number
                </label>
                <p className="text-gray-900">{invoice.invoice_number}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Balance Due
                </label>
                <p className="text-xl font-bold text-red-600">KES {invoice.balance_due.toLocaleString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount *
                </label>
                // In the payment modal input
<input
    type="number"
    step="0.01"
    value={paymentAmount}
    onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}  // Parse to float
    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
    max={invoice.balance_due}
/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method *
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cash">Cash</option>
                  <option value="mpesa">M-Pesa</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={processing || paymentAmount <= 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex space-x-3 pt-4">
        <button onClick={onClose} className="btn-secondary">Close</button>
      </div>
    </div>
  );
}